import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { useSkills, useSkillDetails } from '@/hooks'
import { parseSkillParameters } from '@/lib/skillFrontmatter'
import { cn } from '@/lib/utils'
import type { Skill } from '@/types'

interface SkillPickerProps {
  projectId: string
  value: string[]
  onChange: (skillNames: string[]) => void
  disabled?: boolean
}

function SkillChipGroup({
  heading,
  skills,
  selected,
  onToggle,
  disabled,
}: {
  heading: string
  skills: Skill[]
  selected: Set<string>
  onToggle: (name: string) => void
  disabled: boolean
}) {
  if (skills.length === 0) return null
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{heading}</p>
      <div className="flex flex-wrap gap-1.5">
        {skills.map(skill => {
          const isSelected = selected.has(skill.name.toLowerCase())
          return (
            <button
              key={skill.id}
              type="button"
              disabled={disabled}
              aria-pressed={isSelected}
              title={skill.description ?? undefined}
              onClick={() => onToggle(skill.name)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                isSelected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-muted-foreground hover:text-foreground',
              )}
              data-testid={`skill-chip-${skill.name}`}
            >
              {skill.displayName}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function SkillPicker({ projectId, value, onChange, disabled = false }: SkillPickerProps) {
  const { data: skills = [], isLoading, isError } = useSkills(projectId)

  const selected = useMemo(() => new Set(value.map(name => name.toLowerCase())), [value])
  const dedupedSkills = useMemo(() => {
    const byName = new Map<string, Skill>()
    for (const skill of skills) {
      const key = skill.name.toLowerCase()
      const existing = byName.get(key)
      if (!existing || (existing.isSystem && !skill.isSystem)) byName.set(key, skill)
    }
    return [...byName.values()]
  }, [skills])
  const skillsByName = useMemo(
    () => new Map(dedupedSkills.map(skill => [skill.name.toLowerCase(), skill])),
    [dedupedSkills]
  )

  const uniqueValue = [...new Map(value.map(name => [name.toLowerCase(), name])).values()]
  const selectedSkills = uniqueValue
    .map(name => skillsByName.get(name.toLowerCase()))
    .filter((skill): skill is Skill => !!skill)
  const unresolvedNames = uniqueValue.filter(name => !skillsByName.has(name.toLowerCase()))

  const detailQueries = useSkillDetails(projectId, selectedSkills.map(skill => skill.id))

  const parameterRows = detailQueries
    .map(query => query.data)
    .filter(detail => !!detail)
    .map(detail => ({ skill: detail!, parameters: parseSkillParameters(detail!.content) }))
    .filter(row => row.parameters.length > 0)

  const toggle = (name: string) => {
    const key = name.toLowerCase()
    if (selected.has(key)) {
      onChange(value.filter(existing => existing.toLowerCase() !== key))
    } else {
      onChange([...value, name])
    }
  }

  return (
    <div className="space-y-3" data-testid="skill-picker">
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading skills…</p>
      ) : isError ? (
        <p className="text-sm text-destructive" role="alert">
          Couldn't load this project's skills. Refresh to try again.
        </p>
      ) : skills.length === 0 ? (
        <p className="text-sm text-muted-foreground">No skills available in this project yet.</p>
      ) : (
        <>
          <SkillChipGroup
            heading="System"
            skills={dedupedSkills.filter(skill => skill.isSystem)}
            selected={selected}
            onToggle={toggle}
            disabled={disabled}
          />
          <SkillChipGroup
            heading="Custom"
            skills={dedupedSkills.filter(skill => !skill.isSystem)}
            selected={selected}
            onToggle={toggle}
            disabled={disabled}
          />
        </>
      )}

      {unresolvedNames.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-amber-600" data-testid="unresolved-skills">
          <span>Not found in this project:</span>
          {unresolvedNames.map(name => (
            <Badge key={name} variant="outline" className="font-mono text-amber-600">
              {name}
            </Badge>
          ))}
        </div>
      )}

      {parameterRows.length > 0 && (
        <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-3" data-testid="skill-parameters">
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Skill parameters</p>
          {parameterRows.map(({ skill, parameters }) => (
            <div key={skill.id} className="text-xs">
              <span className="font-semibold text-foreground">{skill.displayName}</span>
              <ul className="mt-0.5 space-y-0.5 pl-4">
                {parameters.map(parameter => (
                  <li key={parameter.name} className="list-disc text-muted-foreground">
                    <span className="font-mono text-foreground">{parameter.name}</span>
                    {parameter.description && <> — {parameter.description}</>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
